import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bot, ExternalLink, Minimize2, Sparkles } from "lucide-react";
import { AiChatPanel } from "@/components/AiChatPanel";
import { tokenStorage } from "@/lib/api";

/**
 * Floating admin AI chip — appears on every /admin/* route so staff can pop
 * the AI Helper open without navigating away from what they're doing. Hidden
 * on the standalone `/admin/ai-helper` page (would be redundant) and on the
 * `/admin` login page (no session yet).
 */
export function AdminAiChipGate() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Only render on authenticated admin routes.
  const isAdminRoute = pathname.startsWith("/admin");
  const isLoginPage = pathname === "/admin" || pathname === "/admin/";
  const isAiHelperPage = pathname === "/admin/ai-helper";
  const hasToken = !!tokenStorage.get?.();

  // Close automatically when navigating between pages so it doesn't feel sticky.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  if (!isAdminRoute || isLoginPage || isAiHelperPage || !hasToken) return null;

  return (
    <>
      {!open && (
        <button
          data-testid="admin-ai-chip"
          onClick={() => setOpen(true)}
          title="Open AI Helper"
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-3.5 py-2.5 rounded-full shadow-lg shadow-black/50 border border-[#D4AF37]/60 bg-gradient-to-br from-[#0A1F44] to-[#0d1428] hover:from-[#D4AF37]/25 hover:to-[#F0D788]/15 text-[#F0D788] text-xs font-semibold transition-all"
        >
          <Bot size={14} />
          AI Helper
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[420px] h-[600px] max-h-[85vh] shadow-2xl shadow-black/60 flex flex-col">
          {/* Extra header actions row on top of the reused panel */}
          <div className="absolute top-2 right-11 z-10 flex items-center gap-1">
            <button
              data-testid="admin-ai-chip-fullscreen"
              onClick={() => {
                setOpen(false);
                navigate("/admin/ai-helper");
              }}
              className="text-white/40 hover:text-white p-1"
              title="Open in full page"
            >
              <ExternalLink size={13} />
            </button>
            <button
              data-testid="admin-ai-chip-minimize"
              onClick={() => setOpen(false)}
              className="text-white/40 hover:text-white p-1"
              title="Minimize"
            >
              <Minimize2 size={13} />
            </button>
          </div>
          <AiChatPanel variant="admin" onClose={() => setOpen(false)} />
        </div>
      )}
    </>
  );
}
